import { useEffect, useState } from "react";
import { MapPin, Mail, Phone, Clock, Loader2, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { fetchProducts, submitInquiry } from "../lib/api";
import { CONTACT } from "../constants/testIds";

const empty = { name: "", company: "", email: "", phone: "", location: "", product_name: "", product_slug: "", requirements: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let cancelled = false;

    async function loadProducts() {
      try {
        const data = await fetchProducts({ limit: 200 });
        if (!cancelled) {
          setProducts(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setProducts([]);
        }
      }
    }

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, []);

  const onProductChange = (event) => {
    const selectedSlug = event.target.value;
    if (!selectedSlug) {
      setForm((prev) => ({ ...prev, product_slug: "", product_name: "" }));
      return;
    }

    const selectedProduct = products.find((item) => item.slug === selectedSlug);
    setForm((prev) => ({
      ...prev,
      product_slug: selectedSlug,
      product_name: selectedProduct?.name || "",
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.requirements) {
      toast.error("Please fill in name, email, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry(form);
      toast.success("Thank you — our team will get back within one business day.");
      setForm(empty);
    } catch {
      toast.error("Unable to submit right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="bg-brand-navy text-white">
        <div className="section-x mx-auto max-w-[1440px] py-24 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <div className="overline text-accent">Get in touch</div>
            <h1 className="mt-3 font-display text-5xl md:text-6xl font-black tracking-tight leading-[1.02]">Let&apos;s engineer something durable together.</h1>
            <p className="mt-5 text-slate-300 text-lg leading-relaxed max-w-xl">
             Share your specifications, timelines and volumes — we&apos;ll respond with a technical proposal within one business day.
             </p>

            <div className="mt-10 space-y-6">
              {[
                { icon: MapPin, label: "Head office", value: "Sector-22, Faridabad Market, Shop No. 19, India" },
                { icon: Phone, label: "Sales", value: "+91 8178799057" },
                { icon: Mail, label: "Email", value: "sales@buildwit.in" },
                { icon: Clock, label: "Business hours", value: "Mon–Sat, 09:00 – 18:30 IST" },
              ].map((c, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-white/10 border border-white/10 text-accent shrink-0"><c.icon className="h-4 w-4"/></span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">{c.label}</div>
                    <div className="mt-1 text-white">{c.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-6">
            <form data-testid={CONTACT.form} onSubmit={onSubmit} className="rounded-2xl bg-white text-slate-900 p-8 md:p-10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.35)]">
              <div className="overline text-accent">Enquiry form</div>
              <h2 className="mt-2 font-display text-2xl font-extrabold text-brand-navy tracking-tight">Tell us about your project</h2>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full name *"><Input value={form.name} onChange={upd("name")}/></Field>
                <Field label="Company"><Input value={form.company} onChange={upd("company")}/></Field>
                <Field label="Email *"><Input type="email" value={form.email} onChange={upd("email")}/></Field>
                <Field label="Phone *"><Input value={form.phone} onChange={upd("phone")}/></Field>
                <Field label="Location" className="md:col-span-2"><Input value={form.location} onChange={upd("location")}/></Field>
                <Field label="Product Interested" className="md:col-span-2">
                  <select
                    value={form.product_slug || ""}
                    onChange={onProductChange}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none"
                  >
                    <option value="">Select a product</option>
                    {products.map((item) => (
                      <option key={item.slug} value={item.slug}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Requirements *" className="md:col-span-2">
                  <Textarea rows={4} value={form.requirements} onChange={upd("requirements")} placeholder="Machinery type, throughput, industry, timelines…"/>
                </Field>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid={CONTACT.submit}
                className="mt-6 w-full h-12 bg-accent hover:bg-accent-hover text-white gap-2 text-sm font-semibold"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
                {loading ? "Sending…" : "Send enquiry"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Map placeholder */}
      <section className="section-x mx-auto max-w-[1440px] py-16">
        <div className="rounded-2xl overflow-hidden border border-slate-200 aspect-[16/6] relative bg-slate-100">
          <iframe
            title="Buildwit Technology Location"
            src="https://www.google.com/maps?q=Sector%2022%20Faridabad%20Market%20Shop%20No%2019&z=15&output=embed"
            className="w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
